import apiClient from './api';
import type { User, AdminCreateUserRequest, UserInviteRequest, UserInviteResponse } from '../types/auth';

export interface ListUsersParams {
  skip?: number;
  limit?: number;
  created_since?: string; // YYYY-MM-DD
  role?: string;
  search?: string;
}

export interface DroneStorageFile {
  kind: 'input' | 'output';
  label: string;
  filename?: string | null;
  source?: string | null;
  bytes: number;
  linked: boolean;
}

export interface DroneStorageFlight {
  id: string;
  name: string;
  status: string;
  created_at: string | null;
  bytes: number;
  files: DroneStorageFile[];
}

export interface DroneStorageReport {
  runs_used: number;
  runs_limit: number;
  storage_used: number;
  storage_limit: number;
  analysis_seconds_used?: number;
  analysis_seconds_limit?: number;
  flights: DroneStorageFlight[];
}

export interface UserLayerRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  endpoint: string | null;
  /** Where the tool appears in the app (from DB `config.ui` when set). */
  ui: string | null;
  in_role_default: boolean;
  override_allow: boolean | null;
  effective_allow: boolean;
}

export interface AnalysisLayerRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  /** Primary API path or URL the tool uses (from DB), e.g. `/api/treedetection` */
  endpoint: string | null;
  ui: string | null;
  is_active: boolean;
  sort_order: number;
  audience_tags?: string[];
  who_sees?: string[];
}

/** Customer mains for compute presets. Layer access uses audience tags, not this list. */
export const SIGNUP_ROLES_FOR_LAYER_DEFAULTS = [
  'blo',
  'plantation_owner',
  'lgu',
  'project_developer',
] as const;

export interface RoleLayerEditorRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string | null;
  endpoint: string | null;
  ui: string | null;
  audience_tags: string[];
  who_sees: string[];
}

export interface UsageEventItem {
  id: string;
  user_id: string;
  user_email: string | null;
  is_guest: boolean;
  client_ip: string | null;
  tool_key: string;
  request_path: string;
  duration_ms: number;
  response_status: number | null;
  created_at: string;
}

export interface RoleComputePresetRow {
  role_key: string;
  daily_hours: number;
  monthly_pack_hours: number;
  max_monthly_extra_hours: number;
  price_base_usd: number;
  price_pack_usd: number;
  price_extra_per_hour_usd: number;
  price_unlimited_usd: number;
  updated_at?: string | null;
}

export interface WhatsNewAdminItem {
  id: string;
  title: string;
  version_label: string | null;
  bullets: string[];
  status: 'draft' | 'published' | string;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export type WhatsNewWriteBody = {
  title?: string;
  version_label?: string | null;
  bullets?: string[];
  status?: 'draft' | 'published';
};

export interface FarmLocationCheckRow {
  farm_id?: string | number;
  name?: string | null;
  ok?: boolean;
  reason?: string | null;
  declared_province_id?: string | null;
  declared_city_id?: string | null;
  declared_barangay_id?: string | null;
  declared_province_name?: string | null;
  declared_city_name?: string | null;
  declared_barangay_name?: string | null;
  centroid?: { lon: number; lat: number } | null;
  gee?: {
    province?: string | null;
    municipality?: string | null;
    barangay?: string | null;
    admin_pcodes?: Record<string, string | null> | null;
  } | null;
}

export interface FarmLocationCheckReport {
  success: boolean;
  monitoring_area?: Record<string, unknown> | null;
  totals?: { farms: number; ok: number; incorrect: number };
  ok?: FarmLocationCheckRow[];
  incorrect?: FarmLocationCheckRow[];
  message?: string;
  error?: string;
}

export type UserPatchBody = Partial<{
  email: string;
  full_name: string;
  role: string;
  sub_role: string | null;
  is_active: boolean;
  is_superuser: boolean;
  organization: string | null;
  compute_limit_mode: string;
  compute_budget_ms_per_day: number | null;
  compute_budget_ms_per_month: number | null;
}>;

/** GET /users/{id}/billing-payment-method-preview — masked PayMongo card (admin only). */
export interface AdminBillingPmPreview {
  has_saved_payment_method: boolean;
  fetched: boolean;
  fetch_detail?: string | null;
  paymongo_payment_method_hint?: string | null;
  kind?: string | null;
  preview_label?: string | null;
  card_brand?: string | null;
  card_last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
}

class AdminService {
  async triggerVegetationProcessing(): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/test_vegetation_processing/');
    return data;
  }

  async triggerForecastProcessing(options?: {
    farm_ids?: string[];
    limit?: number;
    send_weather_mobile?: boolean;
    force_index_forecast?: boolean;
  }): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/test_forecast_processing/', options ?? {});
    return data;
  }

  async clearForecastIndexAlerts(): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/clear_forecast_index_alerts/');
    return data;
  }

  async triggerPestProcessing(options?: {
    farm_ids?: string[];
    limit?: number;
  }): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/test_pest_processing/', options ?? {});
    return data;
  }

  async clearCadiTimeseries(): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/clear_cadi_timeseries/');
    return data;
  }

  async clearPestTimeseries(): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/clear_pest_timeseries/');
    return data;
  }

  async refreshPestDisplayStamp(): Promise<{ status: string; message: string; result?: any }> {
    const { data } = await apiClient.post('/test_pest_display_stamp/');
    return data;
  }

  async listUsers(params: ListUsersParams = {}): Promise<User[]> {
    const { data } = await apiClient.get<User[]>('/users', { params });
    return data;
  }

  /** POST /users/invite — email link for the user to finish signup */
  async inviteUser(body: UserInviteRequest): Promise<UserInviteResponse> {
    const { data } = await apiClient.post<UserInviteResponse>('/users/invite', body);
    return data;
  }

  /** POST /users/create-admin — active immediately, no verification email */
  async createAdminUser(body: AdminCreateUserRequest): Promise<User> {
    const { data } = await apiClient.post<User>('/users/create-admin', body);
    return data;
  }

  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete(`/users/${userId}`);
  }

  /** Admin PATCH /users/{id} — role, compute caps, **is_active** (deactivate), etc. */
  async updateUser(userId: string, body: UserPatchBody): Promise<User> {
    const { data } = await apiClient.patch<User>(`/users/${userId}`, body);
    return data;
  }

  async getUserBillingPaymentMethodPreview(userId: string): Promise<AdminBillingPmPreview> {
    const { data } = await apiClient.get<AdminBillingPmPreview>(
      `/users/${userId}/billing-payment-method-preview`
    );
    return data;
  }

  async getUserLayerEditor(userId: string): Promise<UserLayerRow[]> {
    const { data } = await apiClient.get<UserLayerRow[]>(
      `/admin/users/${userId}/layer-editor`
    );
    return data;
  }

  async putUserLayerOverrides(
    userId: string,
    assignments: { layer_id: string; allow: boolean }[]
  ): Promise<void> {
    await apiClient.put(`/admin/users/${userId}/layer-overrides`, { assignments });
  }

  /** Clears per-user overrides; effective access matches audience tags for this user’s role. */
  async clearUserLayerOverrides(userId: string): Promise<void> {
    await this.putUserLayerOverrides(userId, []);
  }

  /**
   * GEE-check LGU farm centroids vs monitoring area.
   * Long timeout — large catalogs can take several minutes.
   */
  async checkUserFarmLocations(
    userId: string,
    opts?: { digisakaAccessToken?: string; vaultToken?: boolean }
  ): Promise<FarmLocationCheckReport> {
    const { data } = await apiClient.post<FarmLocationCheckReport>(
      `/admin/users/${userId}/farm-location-check`,
      {
        digisaka_access_token: opts?.digisakaAccessToken?.trim() || undefined,
        vault_token: opts?.vaultToken !== false,
      },
      { timeout: 30 * 60 * 1000 }
    );
    return data;
  }

  async listAnalysisLayers(): Promise<AnalysisLayerRow[]> {
    const { data } = await apiClient.get<AnalysisLayerRow[]>('/admin/analysis-layers');
    return data;
  }

  async listLayerTags(): Promise<RoleLayerEditorRow[]> {
    const { data } = await apiClient.get<RoleLayerEditorRow[]>('/admin/layer-tags');
    return data;
  }

  async putLayerTags(layerId: string, audienceTags: string[]): Promise<void> {
    await apiClient.put(`/admin/layer-tags/${encodeURIComponent(layerId)}`, {
      audience_tags: audienceTags,
    });
  }

  async previewLayersForRole(role: string, subRole?: string | null): Promise<RoleLayerEditorRow[]> {
    const { data } = await apiClient.get<RoleLayerEditorRow[]>(
      `/admin/layer-tags/preview/${encodeURIComponent(role)}`,
      { params: subRole ? { sub_role: subRole } : undefined }
    );
    return data;
  }

  async syncAnalysisLayersCatalog(): Promise<{
    ok: boolean;
    catalog_count: number;
    created: string[];
    updated: string[];
    seeded_tags: number;
    seeded_role_assignments: number;
    notes: string[];
  }> {
    const { data } = await apiClient.post('/admin/analysis-layers/sync-catalog');
    return data;
  }

  async createAnalysisLayer(body: {
    key: string;
    name: string;
    description?: string;
    category?: string;
    endpoint?: string;
    audience_tags: string[];
  }): Promise<AnalysisLayerRow> {
    const { data } = await apiClient.post<AnalysisLayerRow>('/admin/analysis-layers', body);
    return data;
  }

  async getRoleLayerEditor(role: string): Promise<RoleLayerEditorRow[]> {
    return this.previewLayersForRole(role);
  }

  async putRoleLayerAssignments(_role: string, _layerIds: string[]): Promise<void> {
    throw new Error('Role default layers are tag-based. Use the layer tag editor.');
  }

  async listUsageToolKeys(): Promise<string[]> {
    const { data } = await apiClient.get<string[]>('/admin/usage-tool-keys');
    return data;
  }

  async listRoleComputePresets(): Promise<RoleComputePresetRow[]> {
    const { data } = await apiClient.get<RoleComputePresetRow[]>('/admin/role-compute-presets');
    return data;
  }

  async updateRoleComputePreset(
    role: string,
    body: Omit<RoleComputePresetRow, 'role_key' | 'updated_at'>
  ): Promise<RoleComputePresetRow> {
    const { data } = await apiClient.put<RoleComputePresetRow>(
      `/admin/role-compute-presets/${encodeURIComponent(role)}`,
      body
    );
    return data;
  }

  async listUsageEvents(params: {
    since?: string;
    until?: string;
    user_id?: string;
    tool_key?: string;
    is_guest?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ total: number; items: UsageEventItem[] }> {
    const { data } = await apiClient.get<{ total: number; items: UsageEventItem[] }>(
      '/admin/usage-events',
      { params }
    );
    return data;
  }

  async listWhatsNew(): Promise<WhatsNewAdminItem[]> {
    const { data } = await apiClient.get<WhatsNewAdminItem[]>('/admin/whats-new');
    return data;
  }

  async createWhatsNew(body: WhatsNewWriteBody): Promise<WhatsNewAdminItem> {
    const { data } = await apiClient.post<WhatsNewAdminItem>('/admin/whats-new', body);
    return data;
  }

  async updateWhatsNew(id: string, body: WhatsNewWriteBody): Promise<WhatsNewAdminItem> {
    const { data } = await apiClient.patch<WhatsNewAdminItem>(
      `/admin/whats-new/${encodeURIComponent(id)}`,
      body
    );
    return data;
  }

  async publishWhatsNew(id: string): Promise<WhatsNewAdminItem> {
    const { data } = await apiClient.post<WhatsNewAdminItem>(
      `/admin/whats-new/${encodeURIComponent(id)}/publish`
    );
    return data;
  }

  async unpublishWhatsNew(id: string): Promise<WhatsNewAdminItem> {
    const { data } = await apiClient.post<WhatsNewAdminItem>(
      `/admin/whats-new/${encodeURIComponent(id)}/unpublish`
    );
    return data;
  }

  async deleteWhatsNew(id: string): Promise<void> {
    await apiClient.delete(`/admin/whats-new/${encodeURIComponent(id)}`);
  }

  async getDroneStorage(userId: string): Promise<DroneStorageReport> {
    const { data } = await apiClient.get<DroneStorageReport>(`/admin/users/${userId}/drone-storage`);
    return data;
  }

  async setDroneQuota(
    userId: string,
    body: { runs_per_month: number; storage_limit_gb: number; analysis_hours_per_month: number }
  ): Promise<void> {
    await apiClient.put(`/admin/users/${userId}/drone-quota`, body);
  }

  async deleteDroneFlight(userId: string, jobId: string): Promise<void> {
    await apiClient.delete(`/admin/users/${userId}/drone-jobs/${jobId}`);
  }

  async deleteDroneStorage(userId: string): Promise<{ deleted: number; skipped: number }> {
    const { data } = await apiClient.delete<{ deleted: number; skipped: number }>(
      `/admin/users/${userId}/drone-storage`
    );
    return data;
  }
}

export default new AdminService();
